/**
 * Widget Order Hooks
 *
 * React Query hooks for widget customer order operations.
 * Provides caching, loading states, and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { WidgetOrderApplicationService } from '../WidgetOrderApplicationService';
import type { OrderFilters } from '../../../types/widget/order';
import type {
  OrderDetailViewModel,
} from '../view-models/OrderViewModel';

/**
 * Query keys for order caching
 */
export const orderKeys = {
  all: ['widget-orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (filters?: OrderFilters) => [...orderKeys.lists(), filters] as const,
  details: () => [...orderKeys.all, 'detail'] as const,
  detail: (orderId: string) => [...orderKeys.details(), orderId] as const,
};

/**
 * Hook to get single order detail
 */
export function useOrder(
  orderService: WidgetOrderApplicationService,
  orderId: string,
  options?: {
    enabled?: boolean;
    refetchInterval?: number;
  }
) {
  return useQuery({
    queryKey: orderKeys.detail(orderId),
    queryFn: () => orderService.getOrder(orderId),
    enabled: options?.enabled ?? true,
    refetchInterval: options?.refetchInterval,
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Hook to get list of orders with filters
 */
export function useOrders(
  orderService: WidgetOrderApplicationService,
  filters?: OrderFilters,
  options?: {
    enabled?: boolean;
  }
) {
  return useQuery({
    queryKey: orderKeys.list(filters),
    queryFn: () => orderService.getOrders(filters),
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

/**
 * Hook to request pickup for an order
 */
export function useRequestPickup(orderService: WidgetOrderApplicationService) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: { requestedPickupDate?: string; notes?: string };
    }) => orderService.requestPickup(orderId, data),
    onSuccess: (updatedOrder: OrderDetailViewModel, variables) => {
      // Update order detail cache
      queryClient.setQueryData(
        orderKeys.detail(variables.orderId),
        updatedOrder
      );

      // Invalidate order lists to refetch
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
}

/**
 * Hook to cancel an order
 */
export function useCancelOrder(orderService: WidgetOrderApplicationService) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      data,
    }: {
      orderId: string;
      data: { reason: string; notes?: string };
    }) => orderService.cancelOrder(orderId, data),
    onSuccess: (_, variables) => {
      // Invalidate order detail and lists
      queryClient.invalidateQueries({ queryKey: orderKeys.detail(variables.orderId) });
      queryClient.invalidateQueries({ queryKey: orderKeys.lists() });
    },
  });
}

/**
 * Hook factory - creates hooks bound to a specific order service instance
 *
 * Usage:
 * ```typescript
 * const orderService = useMemo(
 *   () => new WidgetOrderApplicationService(apiService, customerId),
 *   [apiService, customerId]
 * );
 *
 * const orderHooks = useWidgetOrderHooks(orderService);
 * const { data: order } = orderHooks.useOrder(orderId);
 * ```
 */
export function useWidgetOrderHooks(orderService: WidgetOrderApplicationService) {
  return {
    useOrder: (orderId: string, options?: Parameters<typeof useOrder>[2]) =>
      useOrder(orderService, orderId, options),
    useOrders: (filters?: OrderFilters, options?: Parameters<typeof useOrders>[2]) =>
      useOrders(orderService, filters, options),
    useRequestPickup: () => useRequestPickup(orderService),
    useCancelOrder: () => useCancelOrder(orderService),
  };
}
